'use client';

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Download, 
  AlertCircle, 
  CheckCircle, 
  Loader2, 
  RefreshCw,
  Play,
  Smartphone,
  Monitor
} from 'lucide-react';
import { useVideoDownloadWithRetry } from '@/hooks/useVideoDownloadWithRetry';
import { cn } from '@/lib/utils';

interface VideoDownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: string;
  videoTitle?: string;
  onDownloadComplete?: (downloadUrl: string) => void;
}

export function VideoDownloadDialog({
  isOpen,
  onClose,
  videoId,
  videoTitle = 'this video',
  onDownloadComplete
}: VideoDownloadDialogProps) {
  const {
    qualities,
    isLoadingQualities,
    downloadProgress,
    isDownloading,
    downloadedUrl,
    error,
    retryCount,
    canRetry,
    fetchQualities,
    startDownload,
    retryDownload,
    resetDownload,
    hasExistingDownload,
    getExistingDownload
  } = useVideoDownloadWithRetry();

  const [selectedQuality, setSelectedQuality] = useState<{
    id: number;
    quality: string;
  } | null>(null);

  // Check for existing download and fetch qualities when dialog opens
  useEffect(() => {
    if (isOpen && videoId) {
      console.log('🔍 Dialog opened for videoId:', videoId);
      
      // Check if we already have this video downloaded
      const existingDownload = getExistingDownload(videoId);
      if (existingDownload) {
        console.log('Found existing download:', existingDownload.fileUrl);
        // Still fetch qualities to allow downloading another quality if needed
        fetchQualities(videoId);
      } else {
        console.log('No existing download, fetching qualities...');
        fetchQualities(videoId);
      }
    }
  }, [isOpen, videoId, fetchQualities, getExistingDownload]);

  // Debug: Log qualities state changes
  useEffect(() => {
    console.log('🎬 Qualities state updated:', {
      qualitiesLength: qualities.length,
      isLoadingQualities,
      qualities: qualities.slice(0, 3) // Show first 3 for debugging
    });
  }, [qualities, isLoadingQualities]);

  // Auto-select recommended quality
  useEffect(() => {
    if (qualities.length > 0 && !selectedQuality) {
      setSelectedQuality({
        id: qualities[0].id,
        quality: qualities[0].quality
      });
    }
  }, [qualities, selectedQuality]);

  // Handle download complete
  useEffect(() => {
    if (downloadedUrl && onDownloadComplete) {
      onDownloadComplete(downloadedUrl);
    }
  }, [downloadedUrl, onDownloadComplete]);

  const handleDownload = async () => {
    if (!selectedQuality) return;
    
    await startDownload(videoId, selectedQuality.id, selectedQuality.quality);
  };

  const handlePlayDownloaded = () => {
    if (downloadedUrl && onDownloadComplete) {
      onDownloadComplete(downloadedUrl);
      onClose();
    }
  };

  const handleClose = () => {
    if (!isDownloading) {
      resetDownload();
      setSelectedQuality(null);
      onClose();
    }
  };

  const getProgressText = () => {
    if (!downloadProgress) return 'Preparing...';
    
    switch (downloadProgress.status) {
      case 'pending':
        return 'Initializing download...';
      case 'downloading':
        return 'Downloading video...';
      case 'processing':
        return 'Processing video...';
      case 'completed':
        return 'Download completed!';
      case 'failed':
        return 'Download failed';
      default:
        return 'Processing...';
    }
  };

  const formatFileSize = (bytes: string | number) => {
    const size = typeof bytes === 'string' ? parseInt(bytes) : bytes;
    if (isNaN(size)) return 'Unknown size';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let value = size;
    
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }
    
    return `${value.toFixed(1)} ${units[unitIndex]}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Download Video</DialogTitle>
          <DialogDescription>
            YouTube is not accessible in your region. Download {videoTitle} to watch it locally.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Success Alert */}
          {downloadedUrl && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Download Complete!</AlertTitle>
              <AlertDescription className="text-green-700">
                The video has been downloaded and is ready to play.
              </AlertDescription>
            </Alert>
          )}

          {/* Quality Selection */}
          {!isDownloading && !downloadedUrl && (
            <div className="space-y-3">
              <label className="text-sm font-medium">Select Quality:</label>
              
              {isLoadingQualities ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="ml-2">Loading available qualities...</span>
                </div>
              ) : qualities.length > 0 ? (
                <div className="grid gap-2">
                  {qualities.map((quality) => (
                    <button
                      key={quality.id}
                      onClick={() => setSelectedQuality({ id: quality.id, quality: quality.quality })}
                      className={cn(
                        "flex items-center justify-between rounded-lg border p-3 transition-colors",
                        selectedQuality?.id === quality.id
                          ? "border-primary bg-primary/5"
                          : "border-gray-200 hover:bg-gray-50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {quality.isMobileCompatible ? (
                          <Smartphone className="h-4 w-4" />
                        ) : (
                          <Monitor className="h-4 w-4 text-gray-400" />
                        )}
                        <div className="text-left">
                          <div className="font-medium">{quality.quality}</div>
                          <div className="text-xs text-gray-500">{quality.mime}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatFileSize(quality.size)}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No downloadable qualities found for this video.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Download Progress */}
          {isDownloading && downloadProgress && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span>{getProgressText()}</span>
                <span>{downloadProgress.progress}%</span>
              </div>
              <Progress value={downloadProgress.progress} className="h-2" />
              {downloadProgress.quality && (
                <p className="text-xs text-gray-500">
                  Downloading in {downloadProgress.quality}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {!downloadedUrl ? (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isDownloading}
              >
                Cancel
              </Button>
              
              {/* Show retry button if there's an error and retries are available */}
              {error && canRetry && !isDownloading && (
                <Button
                  variant="outline"
                  onClick={retryDownload}
                  className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry ({retryCount}/{3})
                </Button>
              )}
              
              <Button
                onClick={handleDownload}
                disabled={!selectedQuality || isDownloading || isLoadingQualities || qualities.length === 0}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  resetDownload();
                  fetchQualities(videoId);
                }}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Download Another
              </Button>
              <Button onClick={handlePlayDownloaded}>
                <Play className="mr-2 h-4 w-4" />
                Play Video
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}